import { SHIP_MAX_SPEED } from "../../sim";
import type { Vec2 } from "../../sim/math/vec2";

/**
 * Look-ahead camera (P10.1): the view slides towards where the ship is flying, so a fast ship
 * sees more of what is coming instead of what it left behind. Rendering only; the sim never
 * sees the camera.
 */

/** At this speed (and above) the look-ahead reaches its full size. */
export const LOOK_AHEAD_FULL_SPEED = SHIP_MAX_SPEED;
/** Full look-ahead as a fraction of the half view: the ship sits this far off-centre. */
export const LOOK_AHEAD_MAX_FRACTION = 0.35;
/** Easing time constant (ms): the offset covers ~63 % of a change in this time. */
export const LOOK_AHEAD_EASE_MS = 450;
/** Gentler easing with reduced motion. */
export const LOOK_AHEAD_EASE_REDUCED_MS = 1100;

/** How strongly the view zooms out with speed: the 2008 curve, or a wider one. */
export type SpeedZoom = "classic" | "wide";
/** Camera distance per unit of speed: legacy 50 (OpenGLRenderer.cpp); "wide" adds half again. */
export const SPEED_ZOOM_PER_SPEED: Record<SpeedZoom, number> = { classic: 50, wide: 75 };

/**
 * Where the camera wants to be relative to the ship: along the velocity (not the nose, so
 * turning on the spot doesn't swing the view), growing smoothly with speed. In world units;
 * the half view sizes make it elliptical, so wide viewports look further sideways.
 */
export function lookAheadTarget(vel: Vec2, halfW: number, halfH: number, out: Vec2): Vec2 {
	const speed = Math.hypot(vel.x, vel.y);
	if (speed < 1e-6) {
		out.x = 0;
		out.y = 0;
		return out;
	}
	const t = Math.min(1, speed / LOOK_AHEAD_FULL_SPEED);
	const amount = LOOK_AHEAD_MAX_FRACTION * t * t * (3 - 2 * t); // smoothstep: calm at low speed
	out.x = (vel.x / speed) * amount * halfW;
	out.y = (vel.y / speed) * amount * halfH;
	return out;
}

/** Frame-rate independent exponential easing of `current` towards `target` (in place). */
export function easeTowards(
	current: Vec2,
	target: Vec2,
	dtMs: number,
	timeConstantMs: number,
): void {
	const k = 1 - Math.exp(-Math.max(0, dtMs) / timeConstantMs);
	current.x += (target.x - current.x) * k;
	current.y += (target.y - current.y) * k;
}

/**
 * Free replay camera (I-014): a world point at the viewport's centre and a zoom (screen pixels
 * per world unit), moved by the viewer instead of following a ship. Rendering only.
 */
export interface FreeCamera {
	x: number;
	y: number;
	zoom: number;
}

/** Zooming in goes up to this many screen pixels per world unit. */
export const FREE_CAMERA_MAX_ZOOM = 2;

/** The zoom that shows the whole map in a viewport of this size: the free camera's widest. */
export function fitZoom(viewW: number, viewH: number, world: { width: number; height: number }) {
	return Math.min(viewW / world.width, viewH / world.height);
}

/** The whole map, centred. */
export function fitWorld(
	viewW: number,
	viewH: number,
	world: { width: number; height: number },
): FreeCamera {
	return { x: world.width / 2, y: world.height / 2, zoom: fitZoom(viewW, viewH, world) };
}

function wrap(v: number, size: number): number {
	return ((v % size) + size) % size;
}

/** Drags the view by a screen offset (the map moves with the pointer); wraps like the world. */
export function panFreeCamera(
	cam: FreeCamera,
	dxScreen: number,
	dyScreen: number,
	world: { width: number; height: number },
): void {
	cam.x = wrap(cam.x - dxScreen / cam.zoom, world.width);
	cam.y = wrap(cam.y - dyScreen / cam.zoom, world.height);
}

/**
 * Zooms by `factor`, keeping the world point under `anchor` (screen offset from the viewport's
 * centre) in place, between the whole map and FREE_CAMERA_MAX_ZOOM.
 */
export function zoomFreeCamera(
	cam: FreeCamera,
	factor: number,
	anchor: Vec2,
	viewW: number,
	viewH: number,
	world: { width: number; height: number },
): void {
	const zoom = Math.min(
		FREE_CAMERA_MAX_ZOOM,
		Math.max(fitZoom(viewW, viewH, world), cam.zoom * factor),
	);
	const atX = cam.x + anchor.x / cam.zoom;
	const atY = cam.y + anchor.y / cam.zoom;
	cam.x = wrap(atX - anchor.x / zoom, world.width);
	cam.y = wrap(atY - anchor.y / zoom, world.height);
	cam.zoom = zoom;
}
